/**
 * Registration Success Page
 * Shows confirmation after successful registration
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function RegisterSuccessPage() {
  const location = useLocation();
  const email = location.state?.email as string | undefined;
  const from = location.state?.from as { pathname: string } | undefined;

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(false);

  const handleResendVerification = async () => {
    if (!email || cooldown || isResending) return;

    try {
      setIsResending(true);
      setResendError(null);

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(
        `${apiUrl}/api/auth/resend-verification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to resend verification email');
      }

      setResendSuccess(true);
      setCooldown(true);
      setTimeout(() => setCooldown(false), 60000);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-warm-50">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          {/* Success Icon */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-display font-bold text-warm-900 mb-4">
            Account Created!
          </h1>

          <p className="text-lg text-warm-600 mb-8">
            We've sent a verification email to your inbox. Please click the link in
            the email to verify your account.
          </p>

          <div className="bg-warm-100 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-warm-900 mb-2">What's next?</h3>
            <ol className="text-left text-warm-600 space-y-2">
              <li className="flex gap-2">
                <span className="text-primary-600 font-semibold">1.</span>
                Check your email for the verification link
              </li>
              <li className="flex gap-2">
                <span className="text-primary-600 font-semibold">2.</span>
                Click the link to verify your account
              </li>
              <li className="flex gap-2">
                <span className="text-primary-600 font-semibold">3.</span>
                Sign in and start browsing classes!
              </li>
            </ol>
          </div>

          <div className="space-y-4">
            <Link to="/login" state={from ? { from } : undefined} className="btn-primary w-full py-3 inline-block">
              Go to Sign In
            </Link>
            <p className="text-sm text-warm-500">
              Didn't receive the email?{' '}
              {resendSuccess ? (
                <span className="text-green-600 font-medium">Verification email sent!</span>
              ) : (
                <button
                  onClick={handleResendVerification}
                  disabled={!email || cooldown || isResending}
                  className="text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? 'Sending...' : cooldown ? 'Please wait...' : 'Resend verification email'}
                </button>
              )}
            </p>
            {resendError && (
              <p className="text-sm text-red-500 mt-1">{resendError}</p>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-warm-500 text-sm">
        &copy; {new Date().getFullYear()} Stage Door Access. All rights reserved.
      </footer>
    </div>
  );
}
